import { supabase } from './supabase';

export interface FlatRateCategory {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface FlatRateItem {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  category_id: string | null;
  hours: number;
  order_index: number;
  created_at: string;
  updated_at: string;
  category?: FlatRateCategory;
}

interface BookingFlatRate {
  id: string;
  booking_id: string;
  item_id: string;
  resource_id: string;
  hours: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  item_name?: string;
  item_description?: string;
  item_hours?: number;
  resource_first_name?: string;
  resource_last_name?: string;
  resource_email?: string;
}

export async function getFlatRateItems(businessId: string) {
  const { data, error } = await supabase
    .from('flat_rate_items')
    .select(`
      *,
      category:flat_rate_categories(*)
    `)
    .eq('business_id', businessId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return data as FlatRateItem[];
}

export async function getFlatRateCategories(businessId: string) {
  const { data, error } = await supabase
    .from('flat_rate_categories')
    .select('*')
    .eq('business_id', businessId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return data as FlatRateCategory[];
}

export async function createFlatRateCategory(category: Omit<FlatRateCategory, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('flat_rate_categories')
    .insert([category])
    .select()
    .single();

  if (error) throw error;
  return data as FlatRateCategory;
}

async function updateFlatRateCategory(id: string, updates: Partial<FlatRateCategory>) {
  const { data, error } = await supabase
    .from('flat_rate_categories')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as FlatRateCategory;
}

async function deleteFlatRateCategory(id: string) {
  const { error } = await supabase
    .from('flat_rate_categories')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function createFlatRateItem(item: Omit<FlatRateItem, 'id' | 'created_at' | 'updated_at'>) {
  if (!item.business_id) {
    throw new Error('Unable to create item: Business ID is required');
  }

  // Validate hours
  if (isNaN(item.hours) || item.hours <= 0) {
    throw new Error('Hours must be greater than 0');
  }

  // Ensure category_id is properly handled
  const category_id = item.category_id && item.category_id.trim() !== '' 
    ? item.category_id 
    : null;

  const { data, error } = await supabase
    .from('flat_rate_items')
    .insert([{
      ...item,
      hours: parseFloat(item.hours.toString()),
      category_id
    }])
    .select()
    .single();

  if (error) throw error;
  return data as FlatRateItem;
}

export async function updateFlatRateItem(id: string, updates: Partial<FlatRateItem>) {
  const { data, error } = await supabase
    .from('flat_rate_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as FlatRateItem;
}

export async function deleteFlatRateItem(id: string) {
  const { error } = await supabase
    .from('flat_rate_items')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

async function getBookingFlatRates(bookingId: string) {
  const { data, error } = await supabase
    .from('booking_flat_rates_with_details')
    .select('*')
    .eq('booking_id', bookingId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data as BookingFlatRate[];
}

async function createBookingFlatRate(flatRate: Omit<BookingFlatRate, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('booking_flat_rates')
    .insert([flatRate])
    .select()
    .single();

  if (error) throw error;
  return data as BookingFlatRate;
}

async function updateBookingFlatRate(id: string, updates: Partial<BookingFlatRate>) {
  const { data, error } = await supabase
    .from('booking_flat_rates')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as BookingFlatRate;
}

async function deleteBookingFlatRate(id: string) {
  const { error } = await supabase
    .from('booking_flat_rates')
    .delete()
    .eq('id', id);

  if (error) throw error;
}